import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { Rule } from "@/graphql/__generated__/graphql";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Form } from "@/components/ui/form";
import { Pencil, Trash2 } from "lucide-react";
import { AlertDialogComponent, InputField } from "@/components/shared";
import RenderConditionsComponent from "./RenderConditionsComponent";
import { isManager } from "@/utils/authentication";
import { CircleLoader } from "@/components/shared/loaders";

const createEditRuleFormSchema = (t: TFunction) => z.object({
  name: z.string().min(1, t("schemas:rulebuilder.name.required")),
  description: z.string().max(255, t("schemas:rulebuilder.description.maxLength")).optional(),
  active: z.boolean(),
});

/**
 * Props for the RuleComponent
 * @interface RuleComponentProps
 * @property {Rule} rule - The rule to render
 * @property {any} deleteRuleMutation - Mutation function to delete a rule
 * @property {TFunction} t - Translation function
 * @property {IntlShape} intl - Internationalization object
 */
interface RuleComponentProps {
  rule: Rule;
  deleteRuleMutation: any;
  updateRuleMutation: any;
  t: TFunction;
  intl: IntlShape;
  isProcessing: boolean;
}

/**
 * Renders a single rule with its conditions and delete functionality
 * @param {RuleComponentProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
function RuleComponent({ rule, deleteRuleMutation, updateRuleMutation, t, intl, isProcessing }: RuleComponentProps) {
  const [showRemoveRuleAlert, setShowRemoveRuleAlert] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const defaultFormValues = {
    name: rule.name,
    description: rule.description || "",
    active: rule.active,
  };

  const FormSchema = createEditRuleFormSchema(t);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  /**
   * Shows the delete confirmation dialog
   */
  const handleDeleteRule = () => {
    setShowRemoveRuleAlert(true);
  };

  /**
   * Executes the delete rule mutation
   */
  const confirmDeleteRule = () => {
    deleteRuleMutation({
      variables: { ruleId: rule.id },
      onCompleted: () => {
        setShowRemoveRuleAlert(false);
      }
    });
  };

  /**
   * Toggles the edit mode of the rule
   */
  const toggleEditMode = () => {
    if (!isEditing) {
      form.reset(defaultFormValues);
    }
    setIsEditing(!isEditing);
  };

  /**
   * Handles the submission of the edit form
   * @param {z.infer<typeof FormSchema>} data - The form data
   */
  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    updateRuleMutation({
      variables: { ruleId: rule.id, input: data },
    });
    toggleEditMode();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 px-4 pt-4 pb-0">
        {
          isEditing ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
                <div className="flex flex-col gap-4">
                  <InputField
                    name="name"
                    label={t("settings.rulebuilder.create.inputs.name.label")}
                    placeholder={t("settings.rulebuilder.create.inputs.name.placeholder")}
                    form={form}
                  />
                  <InputField
                    name="description"
                    label={t("settings.rulebuilder.create.inputs.description.label")}
                    placeholder={t("settings.rulebuilder.create.inputs.description.placeholder")}
                    form={form}
                    className="w-full"
                    showOptional={true}
                  />
                  <div className="flex items-center gap-2">
                    <Label htmlFor="active">
                      {t("settings.rulebuilder.create.inputs.active.label")}
                    </Label>
                    <Switch
                      checked={form.watch("active")}
                      {...form.register("active")}
                      onCheckedChange={(checked) => {
                        form.setValue("active", checked);
                      }}
                    />
                  </div>
                </div>
              </form>
            </Form>
          ) : (
            <div className="flex flex-col gap-1">
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                {rule.active ? (
                  <Badge className="text-xs bg-green-300 text-slate-900">{t("common:active")}</Badge>
                ) : (
                  <Badge className="text-xs bg-red-300 text-slate-900">{t("common:inactive")}</Badge>
                )}
                {rule.name}
              </CardTitle>
              <CardDescription className="text-xs">{rule.description}</CardDescription>
            </div>
          )
        }
        {isManager() && (
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" size="sm" onClick={toggleEditMode} disabled={isProcessing}>
                  {t("common:buttons.cancel")}
                </Button>
                <Button variant="default" size="sm" onClick={form.handleSubmit(onSubmit)} disabled={isProcessing} className="gap-2">
                  <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
                  {t("common:buttons.save")}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={toggleEditMode} disabled={isProcessing}>
                  <Pencil className="w-4 h-4 text-slate-500" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeleteRule} className="hover:bg-red-50" disabled={isProcessing}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        <h2 className="text-xs font-medium text-slate-400 my-2">{t("settings.rulebuilder.conditions")}</h2>
        <Separator className="mb-2" />
        <RenderConditionsComponent ruleConditions={rule.rootGroup?.conditions || []} t={t} intl={intl} />
      </CardContent>

      {/* Confirm Delete Alert */}
      <AlertDialogComponent
        open={showRemoveRuleAlert}
        onOpenChange={setShowRemoveRuleAlert}
        actionButtonFn={confirmDeleteRule}
        bodyText={t("settings.rulebuilder.view.deletedialog.description")}
        title={t("settings.rulebuilder.view.deletedialog.title", { ruleName: rule.name })}
        actionButtonText={t("common:buttons.delete")}
      />
    </Card>
  );
}

export default RuleComponent;
